import React, { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Snackbar } from "@mui/material";
import { Customer } from "../../types";
import { customerService } from "../../services/customerService";
import { petService } from "../../services/petService";
import { usePermissions } from "../../hooks/usePermissions";
import { CustomerForm } from "../forms/CustomerForm";
import { CustomerDetailsChange, CustomerDetailsModal } from "./CustomerDetailsModal";
import { customerOrdersUrl } from "../../utils/orderSearchParams";

/** What changed: the customer's own details (via Edit), or their addresses/pets. */
export type CustomerChange = "profile" | CustomerDetailsChange;

interface UseCustomerDetailsOptions {
    /** Something about the customer was changed from the dialog — `customer`
     * is the latest version (with addresses and pets). */
    onCustomerUpdated?: (customer: Customer, change: CustomerChange) => void;
    /** "All {name}'s Orders" was pressed (the dialog has already closed).
     * Without this, it opens the All Orders page filtered to the customer;
     * the All Orders page passes it to filter in place instead. */
    onShowOrders?: (customer: Customer) => void;
}

/**
 * A customer ready for CustomerDetailsModal: always with freshly fetched
 * addresses and pets. Pass the customer you already have (e.g. a list row)
 * or just its id. A failed address or pet fetch falls back to what the
 * customer already carries rather than blocking the dialog.
 */
export async function loadCustomerDetails(customerOrId: Customer | string): Promise<Customer> {
    const customer =
        typeof customerOrId === "string"
            ? await customerService.getCustomerById(customerOrId)
            : customerOrId;
    const id = customer.customer_id;
    const [addresses, pets] = await Promise.all([
        customerService.getCustomerAddresses(id).catch((err) => {
            console.error("Failed to fetch customer addresses:", err);
            return customer.addresses;
        }),
        petService.getCustomerPets(id).catch((err) => {
            console.error("Failed to fetch customer pets:", err);
            return customer.pets;
        }),
    ]);
    return { ...customer, addresses, pets };
}

/**
 * Opens the customer details dialog from any screen — including editing the
 * customer from it. Render `dialog` once, then call `showCustomer(customerOrId)`.
 */
export function useCustomerDetails({ onCustomerUpdated, onShowOrders }: UseCustomerDetailsOptions = {}) {
    const { canWrite } = usePermissions();
    const navigate = useNavigate();
    const [customer, setCustomer] = useState<Customer | null>(null);
    const [open, setOpen] = useState(false);
    // The customer being loaded — lets a screen show a spinner on what was clicked.
    const [loadingCustomerId, setLoadingCustomerId] = useState<string | null>(null);
    const [error, setError] = useState("");
    // The customer open in the edit form, if any.
    const [editing, setEditing] = useState<Customer | null>(null);
    // Reloading the open customer after an edit.
    const [refreshing, setRefreshing] = useState(false);
    // Only the latest click wins if several are made in quick succession.
    const requestRef = useRef(0);
    // Latest callbacks, without re-creating the actions on every render.
    const onUpdatedRef = useRef(onCustomerUpdated);
    onUpdatedRef.current = onCustomerUpdated;
    const onShowOrdersRef = useRef(onShowOrders);
    onShowOrdersRef.current = onShowOrders;

    const showCustomer = useCallback(async (customerOrId: Customer | string) => {
        const request = ++requestRef.current;
        setLoadingCustomerId(
            typeof customerOrId === "string" ? customerOrId : customerOrId.customer_id,
        );
        setError("");
        try {
            const details = await loadCustomerDetails(customerOrId);
            if (request !== requestRef.current) return;
            setCustomer(details);
            setOpen(true);
        } catch (err: any) {
            if (request !== requestRef.current) return;
            setError(err.response?.data?.detail || "Couldn't load this customer. Please try again.");
        } finally {
            if (request === requestRef.current) setLoadingCustomerId(null);
        }
    }, []);

    const close = useCallback(() => {
        setOpen(false);
        setCustomer(null);
    }, []);

    // The edit form saved: show the updated customer (re-read with fresh
    // addresses — the form replaces them) and let the screen refresh.
    const handleSaved = useCallback(async (saved: Customer) => {
        setRefreshing(true);
        let latest: Customer = saved;
        try {
            latest = await loadCustomerDetails(saved.customer_id);
        } catch (err) {
            console.error("Failed to reload the customer after saving:", err);
        } finally {
            setRefreshing(false);
        }
        setCustomer((shown) => (shown?.customer_id === latest.customer_id ? latest : shown));
        onUpdatedRef.current?.(latest, "profile");
    }, []);

    const handleChange = useCallback((changed: Customer, change: CustomerDetailsChange) => {
        onUpdatedRef.current?.(changed, change);
    }, []);

    const showOrders = useCallback(
        (shown: Customer) => {
            close();
            if (onShowOrdersRef.current) onShowOrdersRef.current(shown);
            else navigate(customerOrdersUrl(shown.customer_id));
        },
        [close, navigate],
    );

    const dialog = (
        <>
            <CustomerDetailsModal
                open={open}
                onClose={close}
                customer={customer}
                onEdit={canWrite ? setEditing : undefined}
                refreshing={refreshing}
                onChange={handleChange}
                onShowOrders={showOrders}
            />
            {/* Same form as the Customers page's Edit; opens over the dialog,
                with the addresses as currently shown in it. */}
            <CustomerForm
                open={!!editing}
                onClose={() => setEditing(null)}
                onSubmit={handleSaved}
                customer={editing}
                mode="edit"
            />
            <Snackbar
                open={!!error}
                autoHideDuration={5000}
                onClose={() => setError("")}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="error" variant="filled" onClose={() => setError("")} sx={{ width: "100%" }}>
                    {error}
                </Alert>
            </Snackbar>
        </>
    );

    return { showCustomer, loadingCustomerId, dialog };
}
